import { Component, OnDestroy, OnInit } from '@angular/core';
import { HttpErrorResponse } from '@angular/common/http';
import { TranslateService } from '@ngx-translate/core';
import { Subscription } from 'rxjs';
import { BaseServices } from '../../kuba.services';
import { Application } from '../../../_models/feature';
import { BookingPortalService } from './booking-portal.service';
import {
  BHT_OTHER_CATEGORY_KEY,
  BhtCategoryOption,
  getBhtCategories
} from './booking-portal.config';

/**
 * One service, flattened out of the category tree so the search can look at
 * every entry in one pass. `search` holds the translated category and service
 * label in lower case — the keys are never shown and never searched.
 */
interface CatalogueEntry {
  categoryKey: string;
  categoryLabelKey: string;
  serviceKey: string;
  labelKey: string;
  search: string;
}

/** A rendered block of the catalogue: one category and the services to show for it. */
interface CatalogueGroup {
  categoryKey: string;
  categoryLabelKey: string;
  entries: CatalogueEntry[];
}

/** What the receipt shows after a successful order. */
interface SentOrder {
  /** "BHT-2026-4871" — the same string both order e-mails print. */
  reference: string;
  confirmationSent: boolean;
  categoryLabelKey: string;
  serviceLabelKey: string | null;
  contactName: string;
  contactPhone: string;
}

/** Which contact field a validation message belongs to. */
type ContactField = 'name' | 'email' | 'phone';

/** Lines with a dinHMS-specific wording — see `textKey`. */
type BrandedTextKey =
  | 'INTRO'
  | 'ERROR_NAME'
  | 'NEXT_1'
  | 'DONE_BODY_CONFIRMED'
  | 'DONE_BODY'
  | 'DONE_HEALTH_NOTE';

/**
 * Booking portal — order a BHT service from inside Flexi.
 *
 * Replaces Agil's public WordPress form, and is offered on dinHMS as well,
 * where the order goes to Kuba sales instead of Agil. Because the customer is already
 * authenticated, company name and the person ordering are known rather than
 * typed, leaving the service, and who the service is for.
 *
 * The page is a catalogue on the left and a standing order panel on the
 * right: choosing is step one and is the whole left column, because a customer
 * who visits twice a year does not know Agil's service names by heart. The
 * panel is where the order accumulates, so what is still missing is visible
 * without scrolling and the send button is never merely dead.
 *
 * Two deliberate absences:
 *
 *  - No message box. An order concerns someone's health, and free text is
 *    where clinical detail collects before travelling by e-mail into a
 *    ticketing system. The case handler asks when they make contact. The lock
 *    note in the order panel says so where a user would look for the field.
 *  - No consent checkbox. The web form needs one because anyone on the
 *    internet can submit it; here the user is a customer under contract.
 *
 * Ordering is restricted to an administrator, the business's HSE adviser and
 * its OHS responsible — enforced by the backend, which is the boundary that
 * matters. The tab itself is gated by the feature tree.
 */
@Component({
  selector: 'flexi-booking-portal-form',
  templateUrl: './booking-portal-form.component.html',
  styleUrls: ['./booking-portal.scss'],
  standalone: false
})
export class BookingPortalFormComponent implements OnInit, OnDestroy {
  /**
   * The signed-in application's own catalogue: Agil and dinHMS each have one,
   * and the backend accepts an order only against the same application's list.
   */
  readonly categories: BhtCategoryOption[] = getBhtCategories(BaseServices.ApplicationId);

  private readonly isDinHms = Number(BaseServices.ApplicationId) === Application.DIN_HMS;
  readonly otherCategoryKey = BHT_OTHER_CATEGORY_KEY;

  /** Free-text search over the catalogue. Never submitted anywhere. */
  query = '';

  /** Category whose services are listed while not searching. */
  activeCategoryKey = this.categories[0]?.key ?? '';

  /** What the customer has picked — the two values the backend receives. */
  categoryKey = '';
  serviceKey = '';

  /** The person the service is for. Name defaults to whoever is ordering. */
  contactName = '';
  contactEmail = '';
  contactPhone = '';

  /**
   * A field is only allowed to show its error once the user has left it.
   * Sending with something missing marks all three, so pressing a disabled-
   * looking button still explains itself rather than doing nothing.
   */
  touched: Record<ContactField, boolean> = { name: false, email: false, phone: false };

  isPortalRemoteReadOnly = false;
  submitting = false;
  errorMessage: string | null = null;

  /** Set once an order goes through; the page becomes a receipt. */
  sent: SentOrder | null = null;

  /** What the catalogue column renders, rebuilt on search or category change. */
  groups: CatalogueGroup[] = [];

  private readonly entries: CatalogueEntry[] = this.categories.flatMap(category =>
    category.services.map(service => ({
      categoryKey: category.key,
      categoryLabelKey: category.labelKey,
      serviceKey: service.key,
      labelKey: service.labelKey,
      search: ''
    }))
  );

  private labelsSub?: Subscription;

  constructor(
    private service: BookingPortalService,
    private translate: TranslateService
  ) {}

  ngOnInit(): void {
    this.isPortalRemoteReadOnly =
      BaseServices.IsRmode && String(BaseServices.AbsoluteroleId) === '2';

    // The business is deliberately not shown on the form. BaseServices
    // .BusinessName is empty in some contexts (it has no R-mode fallback the
    // way BusinessId does, and archive.component.ts already guards it for
    // null), so the field rendered as an empty disabled box. The application
    // header names the business anyway, and the backend resolves company name
    // and organisation number from the database rather than trusting anything
    // sent from here.
    //
    // Only the name is available from the session. E-mail and telephone are
    // left for the user to fill in — and they often should be, since ordering
    // on someone else's behalf is the normal case here and a health
    // appointment is arranged with the individual rather than through their
    // employer's inbox.
    this.contactName = BaseServices.Name ?? '';

    this.buildSearchIndex();
  }

  ngOnDestroy(): void {
    this.labelsSub?.unsubscribe();
  }

  /**
   * i18n key for the few lines whose wording depends on where the order goes.
   * Agil's orders open a case with the occupational health provider; dinHMS's
   * are e-mailed to Kuba sales (MDCO), where there is no case to speak of, so
   * dinHMS reads its own copy under FLEXI_BOOKING_PORTAL.DINHMS.
   */
  textKey(key: BrandedTextKey): string {
    return this.isDinHms
      ? `FLEXI_BOOKING_PORTAL.DINHMS.${key}`
      : `FLEXI_BOOKING_PORTAL.${key}`;
  }

  // =========================================================================
  // Catalogue
  // =========================================================================

  get isSearching(): boolean {
    return this.query.trim().length > 0;
  }

  /**
   * True when the catch-all category is the one being browsed. It has no
   * service list, so the grid is replaced by a card that says what ordering
   * under it means — the path has to look deliberate rather than broken,
   * since it is the honest answer for anything Agil has not listed.
   */
  get isBrowsingOther(): boolean {
    return !this.isSearching && this.activeCategoryKey === this.otherCategoryKey;
  }

  get hasNoResults(): boolean {
    return this.isSearching && this.groups.length === 0;
  }

  selectCategory(categoryKey: string): void {
    this.activeCategoryKey = categoryKey;
    this.rebuildGroups();
  }

  onQueryChange(): void {
    this.rebuildGroups();
  }

  clearQuery(): void {
    this.query = '';
    this.rebuildGroups();
  }

  /** Services in a category, for the count on its chip. */
  serviceCount(category: BhtCategoryOption): number {
    return category.services.length;
  }

  // =========================================================================
  // The order
  // =========================================================================

  selectService(categoryKey: string, serviceKey: string): void {
    if (this.isPortalRemoteReadOnly) return;
    this.categoryKey = categoryKey;
    this.serviceKey = serviceKey;
    this.errorMessage = null;
  }

  /** Order under the catch-all category, which carries no service. */
  selectOther(): void {
    if (this.isPortalRemoteReadOnly) return;
    this.categoryKey = this.otherCategoryKey;
    this.serviceKey = '';
    this.errorMessage = null;
  }

  isSelected(categoryKey: string, serviceKey: string): boolean {
    return this.categoryKey === categoryKey && this.serviceKey === serviceKey;
  }

  get hasPick(): boolean {
    return !!this.categoryKey;
  }

  get pickedCategoryLabelKey(): string {
    return this.categories.find(c => c.key === this.categoryKey)?.labelKey ?? '';
  }

  /** Null for the catch-all category, which has nothing more specific to name. */
  get pickedServiceLabelKey(): string | null {
    if (!this.serviceKey) return null;
    return this.categories
      .find(c => c.key === this.categoryKey)
      ?.services.find(s => s.key === this.serviceKey)
      ?.labelKey ?? null;
  }

  /**
   * Clear the pick and go back to browsing the category it came from, so
   * "Change" lands next to the thing that was chosen rather than at the top
   * of the catalogue.
   */
  clearPick(): void {
    if (this.isPortalRemoteReadOnly) return;
    if (this.categoryKey) this.activeCategoryKey = this.categoryKey;
    this.categoryKey = '';
    this.serviceKey = '';
    this.query = '';
    this.errorMessage = null;
    this.rebuildGroups();
  }

  // =========================================================================
  // Validation
  // =========================================================================

  /**
   * One message per contact field, or null. Deliberately loose: a name is
   * anything non-empty, a telephone is eight digits or more in any formatting,
   * and the address only has to look like an address. The case handler rings
   * the number; a stricter rule here would reject more real input than bad.
   */
  errorKey(field: ContactField): string | null {
    switch (field) {
      case 'name':
        return this.contactName.trim()
          ? null : this.textKey('ERROR_NAME');
      case 'email':
        return /^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(this.contactEmail.trim())
          ? null : 'FLEXI_BOOKING_PORTAL.ERROR_EMAIL';
      case 'phone':
        return this.contactPhone.replace(/\D/g, '').length >= 8
          ? null : 'FLEXI_BOOKING_PORTAL.ERROR_PHONE';
    }
  }

  /** The message to show under a field: only after the user has left it. */
  visibleErrorKey(field: ContactField): string | null {
    return this.touched[field] ? this.errorKey(field) : null;
  }

  touch(field: ContactField): void {
    this.touched[field] = true;
  }

  onContactInput(): void {
    this.errorMessage = null;
  }

  get contactIsValid(): boolean {
    return !this.errorKey('name') && !this.errorKey('email') && !this.errorKey('phone');
  }

  get canSend(): boolean {
    if (this.isPortalRemoteReadOnly || this.submitting) return false;
    return this.hasPick && this.contactIsValid;
  }

  /**
   * Why the order cannot be sent yet, in the order the page asks for things.
   * Null once it can — the send button is the only thing left to read.
   */
  get blockerKey(): string | null {
    if (this.isPortalRemoteReadOnly || this.submitting) return null;
    if (!this.hasPick) return 'FLEXI_BOOKING_PORTAL.BLOCKER_PICK';
    if (!this.contactIsValid) return 'FLEXI_BOOKING_PORTAL.BLOCKER_FIELDS';
    return null;
  }

  // =========================================================================
  // Submit
  // =========================================================================

  send(): void {
    if (!this.canSend) {
      // Nothing happens on a press otherwise, which reads as a broken button.
      this.touched = { name: true, email: true, phone: true };
      return;
    }

    this.submitting = true;
    this.errorMessage = null;

    const categoryLabelKey = this.pickedCategoryLabelKey;
    const serviceLabelKey = this.pickedServiceLabelKey;

    this.service.submit(BaseServices.BusinessId, {
      categoryKey: this.categoryKey,
      serviceKey: this.serviceKey || null,
      contactName: this.contactName.trim(),
      contactEmail: this.contactEmail.trim(),
      contactPhone: this.contactPhone.trim(),
    }).subscribe({
      next: res => {
        this.submitting = false;
        this.sent = {
          reference: res.reference,
          confirmationSent: res.confirmationSent,
          categoryLabelKey,
          serviceLabelKey,
          contactName: this.contactName.trim(),
          contactPhone: this.contactPhone.trim(),
        };
      },
      error: (err: HttpErrorResponse) => {
        this.submitting = false;
        this.errorMessage = err.error?.detail
          ?? this.translate.instant('FLEXI_BOOKING_PORTAL.SEND_FAILED');
      }
    });
  }

  /**
   * Back to an empty catalogue. The contact fields survive: ordering two
   * services for the same employee in one sitting is the common second order,
   * and retyping their details would be the only work left on the page.
   */
  orderAnother(): void {
    this.sent = null;
    this.categoryKey = '';
    this.serviceKey = '';
    this.query = '';
    this.activeCategoryKey = this.categories[0]?.key ?? '';
    this.errorMessage = null;
    this.rebuildGroups();
  }

  // =========================================================================
  // Internals
  // =========================================================================

  /**
   * Search matches the translated label rather than the key, so the index is
   * language-dependent. `stream` rather than `instant` for two reasons: it
   * waits for the translation file, which `instant` does not — at first paint
   * the index would otherwise be built from the raw keys — and it re-emits
   * when the user switches language.
   */
  private buildSearchIndex(): void {
    const keys = Array.from(new Set(
      this.entries.flatMap(e => [e.labelKey, e.categoryLabelKey])
    ));

    this.labelsSub = this.translate.stream(keys).subscribe((labels: Record<string, string>) => {
      for (const entry of this.entries) {
        entry.search =
          `${labels[entry.labelKey] ?? ''} ${labels[entry.categoryLabelKey] ?? ''}`.toLowerCase();
      }
      this.rebuildGroups();
    });
  }

  /**
   * Searching looks across every category and keeps the groups as headings;
   * browsing shows one category and no heading, because the chip above it
   * already names it.
   */
  private rebuildGroups(): void {
    const query = this.query.trim().toLowerCase();

    if (query) {
      this.groups = this.categories
        .map(category => ({
          categoryKey: category.key,
          categoryLabelKey: category.labelKey,
          entries: this.entries.filter(
            e => e.categoryKey === category.key && e.search.includes(query)
          ),
        }))
        .filter(group => group.entries.length > 0);
      return;
    }

    const active = this.categories.find(c => c.key === this.activeCategoryKey);
    this.groups = active && active.services.length > 0
      ? [{
          categoryKey: active.key,
          categoryLabelKey: active.labelKey,
          entries: this.entries.filter(e => e.categoryKey === active.key),
        }]
      : [];
  }
}
